import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import {
  Sun,
  Moon,
  Menu,
  X,
  FileDown,
  Heart,
  FlaskConical,
  GitCompare,
  LogOut,
  LayoutGrid,
  Home,
  ChevronDown,
  Check,
  Plus,
  Settings,
  Shield,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useThemeStore } from '../../stores/useThemeStore';
import { useFavoriteActions } from '../../hooks/useFavoriteActions';
import { useCartActions } from '../../hooks/useCartActions';
import { useCompareActions } from '../../hooks/useCompareActions';
import { useSettingsStore } from '../../stores/useSettingsStore';
import { useTranslation, type LangCode } from '../../i18n';
import { useOfflineStatus } from '../../hooks/useOfflineStatus';
import { useToast } from '../../context/ToastContext';
import NavbarSearch from './NavbarSearch';
import FlagIcon from '../common/FlagIcon';

const LANGUAGES = [
  { code: 'en', label: 'English', countryCode: 'gb' },
  { code: 'de', label: 'Deutsch', countryCode: 'de' },
  { code: 'fr', label: 'Fran\u00e7ais', countryCode: 'fr' },
  { code: 'it', label: 'Italiano', countryCode: 'it' },
  { code: 'es', label: 'Espa\u00f1ol', countryCode: 'es' },
  { code: 'nl', label: 'Nederlands', countryCode: 'nl' },
  { code: 'pl', label: 'Polski', countryCode: 'pl' },
];

const MAX_CARTS = Infinity;

const Header = () => {
  const { isDark, toggleTheme } = useThemeStore();
  const { isLoggedIn, user, hasRole, logout } = useAuth();
  const { favorites } = useFavoriteActions(isLoggedIn);
  const { cart, carts, activeCartId, switchCart, createCart } = useCartActions(isLoggedIn);
  const { compareList } = useCompareActions(isLoggedIn);
  const multiCartEnabled = useSettingsStore((s) => s.multiCartEnabled);
  const navigate = useNavigate();
  const { lang: language, setLang: setLanguage, t } = useTranslation();
  const { isOnline } = useOfflineStatus();
  const toaster = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [cartDropdownOpen, setCartDropdownOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const currentLang = LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'text-vivatis-600 bg-vivatis-50 dark:bg-vivatis-950/40'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800'
    }`;

  const navItems = [
    { to: '/', label: t.nav.home, icon: Home },
    { to: '/products', label: t.nav.products, icon: LayoutGrid },
    { to: '/wizard', label: t.nav.wizard, icon: FlaskConical },
    { to: '/favorites', label: t.actions.favoritesLabel, icon: Heart, badge: favorites.length },
    { to: '/compare', label: t.actions.compare, icon: GitCompare, badge: compareList.length },
    { to: '/cart', label: t.actions.cartLabel, icon: FileDown, badge: cart.length },
  ];

  const activeCartObj = carts.find((c) => c.id === activeCartId);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div className="mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 shrink-0">
            <img
              src="/VIV_Logo_main.svg"
              alt="Vivatis Pharma"
              className="h-6 sm:h-8 w-auto dark:brightness-0 dark:invert"
            />
            <span className="text-xs font-medium text-vivatis-400 mt-1">PDB</span>
          </NavLink>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center gap-1 ml-4 2xl:ml-8">
            {navItems.map((item) =>
              'external' in item && item.external ? (
                <a key={item.to} href={item.to} target="_blank" rel="noopener noreferrer" className={navLinkClass({ isActive: false })}>
                  <item.icon size={16} />
                  <span className="hidden 2xl:inline">{item.label}</span>
                </a>
              ) : (
                <NavLink key={item.to} to={item.to} className={navLinkClass} title={item.label}>
                  <item.icon size={16} />
                  <span className="hidden 2xl:inline">{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-vivatis-600 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              )
            )}

            {/* Cart switcher dropdown */}
            <div className="relative">
                {multiCartEnabled ? (
                  <button
                    type="button"
                    onClick={() => setCartDropdownOpen(!cartDropdownOpen)}
                    className="flex items-center gap-2 ml-2 pl-2 pr-2.5 py-1.5 rounded-full bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/30 dark:text-vivatis-200 border border-vivatis-200/70 dark:border-vivatis-700/50 hover:bg-vivatis-100 dark:hover:bg-vivatis-900/50 transition-colors cursor-pointer"
                    title={activeCartObj?.name ?? 'Document'}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    <span className="text-xs font-semibold max-w-[6rem] 2xl:max-w-[10rem] truncate">
                      {activeCartObj?.name ?? 'Document'}
                    </span>
                    {activeCartObj && activeCartObj.products.length > 0 && (
                      <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-vivatis-600 text-white text-[10px] font-bold">
                        {activeCartObj.products.length}
                      </span>
                    )}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${cartDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <NavLink
                    to="/cart"
                    className="flex items-center gap-2 ml-2 pl-2 pr-2.5 py-1.5 rounded-full bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/30 dark:text-vivatis-200 border border-vivatis-200/70 dark:border-vivatis-700/50 hover:bg-vivatis-100 dark:hover:bg-vivatis-900/50 transition-colors"
                    title={activeCartObj?.name ?? 'Document'}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    <span className="text-xs font-semibold max-w-[6rem] 2xl:max-w-[10rem] truncate">
                      {activeCartObj?.name ?? 'Document'}
                    </span>
                    {activeCartObj && activeCartObj.products.length > 0 && (
                      <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-vivatis-600 text-white text-[10px] font-bold">
                        {activeCartObj.products.length}
                      </span>
                    )}
                  </NavLink>
                )}

                {multiCartEnabled && cartDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setCartDropdownOpen(false)} />
                    <div data-testid="cart-switcher-menu" className="absolute left-0 z-20 mt-1 w-56 max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain scroll-py-2 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                      {carts.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors cursor-pointer ${
                            c.id === activeCartId
                              ? 'bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/30 dark:text-vivatis-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => {
                            switchCart(c.id);
                            setCartDropdownOpen(false);
                          }}
                        >
                          {c.id === activeCartId && <Check className="w-3.5 h-3.5 shrink-0" />}
                          {c.id !== activeCartId && <span className="w-3.5 shrink-0" />}
                          <span className="flex-1">{c.name}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                            {c.products.length}
                          </span>
                        </button>
                      ))}
                      {carts.length < MAX_CARTS && (
                        <>
                          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => {
                              createCart(`Document ${carts.length + 1}`);
                              setCartDropdownOpen(false);
                              navigate('/cart');
                            }}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>{t.cart.newDocument}</span>
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-1 sm:gap-2 ml-auto pl-2 sm:pl-4">
            {/* Search */}
            <div className="hidden sm:block">
              <NavbarSearch />
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* Language selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setLangOpen(!langOpen)}
                title={!isOnline ? t.common.offlineLanguageHint : undefined}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
              >
                <FlagIcon code={currentLang.countryCode} size={16} />
                <span className="hidden sm:inline text-xs font-medium uppercase">{currentLang.code}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>

              {langOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLangOpen(false)} />
                  <ul className="absolute right-0 z-20 mt-1 w-44 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                    {LANGUAGES.map((lang) => (
                      <li key={lang.code}>
                        <button
                          type="button"
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors cursor-pointer ${
                            language === lang.code
                              ? 'bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/30 dark:text-vivatis-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => {
                            if (lang.code !== language) {
                              setLanguage(lang.code as LangCode);
                              if (!isOnline) {
                                toaster.show(t.common.offlineLanguageHint, 'info', 6000);
                              }
                            }
                            setLangOpen(false);
                          }}
                        >
                          <FlagIcon code={lang.countryCode} size={16} />
                          <span>{lang.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Profile dropdown */}
            <div className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 ml-1 rounded-lg px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                title={user?.name ?? ''}
              >
                <div className="w-8 h-8 rounded-full bg-vivatis-600 text-white flex items-center justify-center text-xs font-bold">
                  {user ? user.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() : '??'}
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden 2xl:block">
                  {user?.name ?? ''}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)} />
                  <div className="absolute right-0 z-20 mt-1 w-56 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                    {user && (
                      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {user.name}
                        </p>
                        {user.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/settings');
                      }}
                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <Settings size={16} />
                      <span>{t.settings.title}</span>
                    </button>
                    {hasRole('admin') && (
                      <a
                        href={`${import.meta.env.VITE_BACKEND_URL}/admin`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setProfileOpen(false)}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                      >
                        <Shield size={16} />
                        <span>{t.nav.admin}</span>
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Logout (hidden on small mobile — available in burger menu) */}
            <button
              onClick={logout}
              className="hidden sm:block p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-950/30 transition-colors"
              aria-label="Logout"
            >
              <LogOut size={18} />
            </button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((prev) => !prev)}
              className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain scroll-py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
        >
          <div className="mx-auto px-4 py-3 space-y-1">
            {/* Mobile search */}
            <div className="mb-2">
              <NavbarSearch onNavigate={() => setMobileOpen(false)} />
            </div>

            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={navLinkClass}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-vivatis-600 rounded-full">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Mobile cart switcher */}
            {multiCartEnabled && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-1">
                {t.actions.cartLabel}
              </p>
              {carts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    c.id === activeCartId
                      ? 'text-vivatis-600 bg-vivatis-50 dark:bg-vivatis-950/40'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => {
                    switchCart(c.id);
                    setMobileOpen(false);
                    navigate('/cart');
                  }}
                >
                  <FileDown size={16} />
                  <span className="flex-1">{c.name}</span>
                  <span className="text-xs text-gray-400">{c.products.length}</span>
                </button>
              ))}
              {carts.length < MAX_CARTS && (
                <button
                  type="button"
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => {
                    createCart(`Document ${carts.length + 1}`);
                    setMobileOpen(false);
                    navigate('/cart');
                  }}
                >
                  <Plus size={16} />
                  <span>{t.cart.newDocument}</span>
                </button>
              )}
            </div>
            )}

            {/* Mobile settings + admin links */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 space-y-1">
              <NavLink
                to="/settings"
                onClick={() => setMobileOpen(false)}
                className={navLinkClass}
              >
                <Settings size={18} />
                <span>{t.settings.title}</span>
              </NavLink>
              {hasRole('admin') && (
                <a
                  href={`${import.meta.env.VITE_BACKEND_URL}/admin`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className={navLinkClass({ isActive: false })}
                >
                  <Shield size={18} />
                  <span>{t.nav.admin}</span>
                </a>
              )}
              <button
                type="button"
                onClick={() => { setMobileOpen(false); logout(); }}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
              >
                <LogOut size={18} />
                <span>{t.nav.logout}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
